// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import 'reflect-metadata'

import { Exclude, Expose, Transform, TransformationType, Type } from 'class-transformer'
import { CardString } from 'pokersolver'
import { Subject, Subscriber } from 'rxjs'
import { v4 as nanoid } from 'uuid'

import { CannotPlayActionError } from '../error/board/CannotPlayActionError'
import { BoardEvent, BoardEvents } from '../messaging/event/BoardEvent'
import { logger } from '../util'
import { Bet, BetKind } from './Bet'
import { Blind } from './Blind'
import { Deck } from './Deck'
import { Player } from './Player'
import { Pot, PotKind, PotWinner } from './Pot'
import { BoardStates } from './states/BoardState'
import { BoardActions } from './states/BoardStateAction'
import { BoardReducer } from './states/BoardStateReducer'

import type { BoardState } from './states/BoardState.types'
const seededSeats = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

export class Board {
  @Type(() => Deck)
  deck: Deck
  @Type(() => String)
  communityCards: CardString[] = []
  @Expose()
  @Type(() => Bet)
  @Transform(({ value, key, obj, type }) => {
    if (type === TransformationType.CLASS_TO_PLAIN) {
      logger.trace('[Board.Transform.CLASS_TO_PLAIN]', key, obj)
      logger.trace('[Board.Transform.CLASS_TO_PLAIN] bets.len', value.length, value)
    } else if (type === TransformationType.PLAIN_TO_CLASS) {
      logger.trace('[Board.Transform.PLAIN_TO_CLASS]', key)
      logger.trace('[Board.Transform.PLAIN_TO_CLASS] bets.len', value.length, value)
    }

    return value
  })
  public bets: Bet[] = []
  @Type(() => Blind)
  public blinds: Blind
  @Type(() => Pot)
  pot: Pot
  @Type(() => Pot)
  sidePots: Pot[] = []
  @Type(() => BoardStates)
  @Transform(({ value, key, obj, type }) => {
    if (type === TransformationType.CLASS_TO_PLAIN) {
      logger.trace('[Board.Transform.CLASS_TO_PLAIN]', key, obj)
      logger.trace('[Board.Transform.CLASS_TO_PLAIN] boardState', value)
    } else if (type === TransformationType.PLAIN_TO_CLASS) {
      logger.trace('[Board.Transform.PLAIN_TO_CLASS]', key)
      logger.trace('[Board.Transform.PLAIN_TO_CLASS] boardState', value)
    }

    return value
  })
  private state: BoardState = BoardStates.PreDeal

  @Exclude()
  @Type(() => Subject)
  public event$ = new Subject<BoardEvent>()

  constructor(
    blinds: Blind,
    public id: string = nanoid(),
    subscriber?: Subscriber<BoardEvent>
  ) {
    this.blinds = blinds
    if (subscriber) {
      this.event$.subscribe(subscriber)
    }

    logger.trace(`[Board.constructor] ${JSON.stringify(this.state)}}`)

    this.setupNewHand(this.blinds, { clearCommunityCards: true })
  }

  get playersOnlyCalledBB() {
    return this.bets
      .filter(Boolean)
      .every(
        (b) =>
          [BetKind.BLANK, BetKind.BIGBLIND, BetKind.CALL, BetKind.SMALLBLIND].includes(b._kind) ||
          [BetKind.BLANK, BetKind.BIGBLIND, BetKind.CALL, BetKind.SMALLBLIND].includes(b.kind)
      )
  }

  /**
   * Resets the board
   * @param blinds Blinds required for this new round
   */
  setupNewHand(blinds: Blind, options?: { cards?: CardString[]; clearCommunityCards?: boolean }) {
    this.bets = seededSeats.map(() => new Bet(0, BetKind.BLANK))
    this.deck = new Deck()
    if (options?.cards) {
      this.deck.cards = options.cards
    } else {
      this.deck.shuffleSattolo()
    }
    this.pot = new Pot(0, [], PotKind.MAIN)
    this.sidePots = []
    this.blinds = blinds
    if (options?.clearCommunityCards) {
      this.communityCards = []
    }
    this.state = BoardStates.PreDeal

    this.event$.next(BoardEvents.newHandEvent(this))
  }

  /**
   * Setup players for the main pot
   */
  setPlayers(players: Player[]) {
    this.pot.players = players
  }

  /**
   * @returns whether new players can join
   */
  canSeatNewPlayers() {
    return BoardStates.isPreDeal(this.state)
  }

  /**
   * Moves board to next state
   */
  nextAction(dealNextCards = true) {
    logger.trace(`[Board.nextAction] ${JSON.stringify(this.state)}}`)

    if (BoardStates.isPreDeal(this.state)) {
      this.state = BoardReducer(BoardActions.DealCards, this.state)
    } else if (BoardStates.isDeal(this.state)) {
      this.state = BoardReducer(BoardActions.DealFlop, this.state)
      if (dealNextCards) {
        this.dealFlop()
      }
    } else if (BoardStates.isFlop(this.state)) {
      this.state = BoardReducer(BoardActions.DealTurn, this.state)
      if (dealNextCards) {
        this.dealTurn()
      }
    } else if (BoardStates.isTurn(this.state)) {
      this.state = BoardReducer(BoardActions.DealRiver, this.state)
      if (dealNextCards) {
        this.dealRiver()
      }
    } else if (BoardStates.isRiver(this.state)) {
      this.state = BoardReducer(BoardActions.EvalShowdown, this.state)
    } else if (BoardStates.isShowdown(this.state)) {
      this.state = BoardReducer(BoardActions.ResetBoard, this.state)
      this.setupNewHand(this.blinds, { clearCommunityCards: false })
    }
  }

  /**
   * Deals two hole cards
   *
   * @returns two hole cards
   */
  dealHoleCards() {
    logger.trace('[Board.dealHoleCards]')
    if (BoardStates.isDeal(this.state)) {
      this.event$.next(BoardEvents.dealtHoleCardsEvent(this))

      return this.deck.cards.splice(0, 2)
    }
    throw new CannotPlayActionError(BoardActions.DealCards, this.state.description)
  }

  /**
   * Deals the flop after burning one
   *
   * @returns the flop cards
   */
  dealFlop() {
    logger.trace('[Board.dealFlop]')
    if (BoardStates.isFlop(this.state)) {
      this.deck.burnOne()
      const flop = this.deck.cards.splice(0, 3)
      this.communityCards = this.communityCards.concat(flop)

      this.event$.next(BoardEvents.dealtFlop(this))
      return flop
    }
    throw new CannotPlayActionError(BoardActions.DealFlop, this.state.description)
  }

  /**
   * Deals the turn card after burning one.
   *
   * @returns the turn
   */
  dealTurn() {
    logger.trace('[Board.dealTurn]')
    if (BoardStates.isTurn(this.state)) {
      this.deck.burnOne()
      const turn = this.deck.cards.splice(0, 1)[0]
      this.communityCards.push(turn)

      this.event$.next(BoardEvents.dealtTurn(this))
      return turn
    }
    throw new CannotPlayActionError(BoardActions.DealTurn, this.state.description)
  }

  /**
   * Deals the river card after burning one.
   *
   * @returns the river card
   */
  dealRiver() {
    logger.trace('[Board.dealRiver]')
    if (BoardStates.isRiver(this.state)) {
      this.deck.burnOne()
      const river = this.deck.cards.splice(0, 1)[0]
      this.communityCards.push(river)

      this.event$.next(BoardEvents.dealtRiver(this))
      return river
    }
    throw new CannotPlayActionError(BoardActions.DealRiver, this.state.description)
  }

  evalShowdown() {
    logger.trace('[Board.evalShowdown]')
    if (BoardStates.isShowdown(this.state)) {
      this.event$.next(BoardEvents.showdown(this))

      return
    }
    throw new CannotPlayActionError(BoardActions.EvalShowdown, this.state.description)
  }

  isPreDeal() {
    return BoardStates.isPreDeal(this.state)
  }

  isDeal() {
    return BoardStates.isDeal(this.state)
  }

  isFlop() {
    return BoardStates.isFlop(this.state)
  }

  isTurn() {
    return BoardStates.isTurn(this.state)
  }

  isRiver() {
    return BoardStates.isRiver(this.state)
  }

  isShowdown() {
    return BoardStates.isShowdown(this.state)
  }

  /**
   * Get the smallest bet on the board.
   *
   * @returns Smallest bet on the board ignoring BigBlinds
   */
  smallestBetWithoutBB() {
    return this.bets
      .filter((b) => b !== null && typeof b !== 'undefined' && b.kind !== 'bigblind')
      .reduce((accBet, bet) => {
        if (bet.amount !== 0) {
          return accBet < bet.amount ? accBet : bet.amount
        }
        return accBet
      }, Number.MAX_VALUE)
  }
  /**
   * Get the smallest bet on the board.
   *
   * @returns Smallest bet on the board ignoring SmallBlind
   */
  smallestBetWithoutSB() {
    return this.bets
      .filter((b) => b !== null && typeof b !== 'undefined' && b.kind !== 'smallblind')
      .reduce((accBet, bet) => {
        if (bet.amount !== 0) {
          return accBet < bet.amount ? accBet : bet.amount
        }
        return accBet
      }, Number.MAX_VALUE)
  }
  /**
   * Get the smallest bet on the board.
   *
   * @returns Smallest bet on the board ignoring SmallBlind and BigBlinds
   */
  smallestBetWithoutSBandBB() {
    return this.bets
      .filter((b) => b !== null && typeof b !== 'undefined' && b.kind !== 'smallblind' && b.kind !== 'bigblind')
      .reduce((accBet, bet) => {
        if (bet.amount !== 0) {
          return accBet < bet.amount ? accBet : bet.amount
        }
        return accBet
      }, Number.MAX_VALUE)
  }

  /**
   * Get the smallest bet on the board.
   *
   * @returns Smallest bet on the board or Number.MAX_VALUE
   */
  smallestBet() {
    return this.bets
      .filter((b) => b !== null && typeof b !== 'undefined')
      .reduce((accBet, bet) => {
        if (bet.amount !== 0) {
          return accBet < bet.amount ? accBet : bet.amount
        }
        return accBet
      }, Number.MAX_VALUE)
  }

  /**
   * Get the biggest bet on the board
   *
   * @returns biggest bet on board
   */
  biggestBet() {
    return this.bets
      .filter((b) => b !== null && typeof b !== 'undefined')
      .reduce((accBet, bet) => {
        return accBet > bet.amount ? accBet : bet.amount
      }, 0)
  }

  /**
   * Get the second biggest bet on the board
   *
   * @returns second biggest bet on board
   */
  secondBiggestBet() {
    let biggest = this.biggestBet()
    let nextbiggest = 0
    for (let i = 0; i < this.bets.length; i++) {
      if (this.bets[i] && this.bets[i].amount > biggest) {
        nextbiggest = biggest
        biggest = this.bets[i].amount
      } else if (this.bets[i] && this.bets[i].amount > nextbiggest && this.bets[i].amount !== biggest) {
        nextbiggest = this.bets[i].amount
      }
    }
    return nextbiggest
  }

  /**
   * Get the last bet on the board by filtering BET and RAISE
   * and then finding the largest amount
   *
   * @returns the last bet
   */
  lastBet() {
    let lastBetAmount = this.bets
      .filter((b) => b !== null && typeof b !== 'undefined')
      .filter((b) => b.kind === BetKind.BET || b.kind === BetKind.RAISE || b.kind === BetKind.BIGBLIND)
      .reduce((accBet, bet) => {
        return accBet > bet.amount ? accBet : bet.amount
      }, 0)

    if (lastBetAmount === 0 && this.isDeal()) {
      logger.trace(`[Board.lastBet] this.blinds.big ${this.blinds.big}`)
      lastBetAmount = this.blinds.big
    }

    // @TODO: needed?
    if (lastBetAmount === 0) {
      logger.trace(`[Board.lastBet] this.blinds.big ${this.blinds.big}`)
      lastBetAmount = this.blinds.big
    }

    logger.trace(`[Board.lastBet] lastBetAmount ${lastBetAmount}`)
    return lastBetAmount
  }

  /**
   * Pass without raising or meeting a raise. Will put bet
   * amount at previous value or 0 if no previous bet.
   *
   * @param seatId seat offset at table in Table.players[]
   */
  doCheckForPlayerAt(seatId: number) {
    logger.trace(`[Board.doCheckForPlayerAt] ${this.bets.length} ${seatId}`)

    if (!this.bets[seatId]) {
      this.bets[seatId] = new Bet(0, BetKind.CHECK)
    } else {
      this.bets[seatId].kind = BetKind.CHECK
    }
  }

  /**
   * Match biggest bet at the table.
   *
   * @param seatId seat offset at table in Table.players[]
   */
  doCallForPlayerAt(seatId: number) {
    const bet = new Bet(this.biggestBet(), BetKind.CALL)
    logger.trace(`[Board.doCallForPlayerAt] ${seatId} ${bet.kind} ${bet.amount}`)
    this.bets[seatId] = bet
  }

  /**
   * Set bet for player at seat
   *
   * @param seatId seat offset at table in Table.players[]
   * @param bet bet to set
   */
  doBetForPlayerAt(seatId: number, bet: Bet) {
    if (bet?.kind) {
      logger.trace(`[Board.doBetForPlayerAt] ${seatId} ${bet.kind} ${bet.amount}`)
    } else {
      logger.trace(`[Board.doBetForPlayerAt] ${seatId} bet undefined`)
    }

    this.bets[seatId] = bet
  }

  /**
   * Set bet for player at seat
   *
   * @param seatId seat offset at table in Table.players[]
   * @param amount to reduce
   */
  adjustBetForPlayerAt(seatId: number, amount: number) {
    if (this.bets[seatId]) {
      this.bets[seatId].amount -= amount
      if (this.bets[seatId].amount <= 0) {
        this.bets[seatId] = undefined
      }
    }
  }

  adjustBetForBigBlind(seatId: number, amount: number) {
    if (this.bets[seatId]) {
      this.bets[seatId].amount = amount
    }
  }

  addPlayerBettingSeatAt(seatId: number) {
    let bet
    if (this.bets[seatId] !== null) {
      bet = this.bets[seatId]
      delete this.bets[seatId]
    }
    this.bets[seatId + 1] = bet
  }

  /**
   * Get bet for player at seat
   *
   * @param seatId seat offset at table in Table.players[]
   * @returns bet at seat or undefined
   */
  getBetForPlayerAt(seatId: number) {
    logger.trace(`[Board.getBetForPlayerAt] ${seatId}`)
    return this.bets[seatId]
  }

  /**
   * See if player has bet at seat
   *
   * @param seatId seat offset at table in Table.players[]
   * @returns true if player has bet
   */
  playerHasBetAt(seatId: number) {
    if (this.bets[seatId] && this.bets[seatId].kind !== BetKind.BLANK) {
      return true
    }
    return false
  }

  /**
   * See if board has any bets
   *
   * @returns true if bets have been made this round
   */
  hasBets() {
    const filteredBets = this.bets.filter((b) => b !== null && typeof b !== 'undefined' && b.kind !== BetKind.BLANK)
    logger.trace(`[Board.hasBets] filteredBets.length ${filteredBets.length}`)
    return filteredBets.length > 0
  }

  /**
   * Checks the board for bets that are not checks and greater than 0. This
   * is needed to evaluate for sidepots after all-in
   *
   * @returns true if there are still bets on the board
   */
  hasNonZeroBets() {
    const bets = this.bets
      .filter((b) => b !== null && typeof b !== 'undefined')
      .reduce((acc, bet) => {
        acc += bet.amount
        return acc
      }, 0)
    return bets > 0
  }

  hasBetsNoChecks() {
    logger.trace('[Board.hasBetsNoChecks]')
    const betsNoChecks = this.bets
      .filter((b) => b !== null && typeof b !== 'undefined' && b.kind !== BetKind.BLANK)
      .filter((b) => b.kind !== BetKind.CHECK)

    logger.trace(`[Board.hasBetsNoChecks] .length ${betsNoChecks.length}`)
    return betsNoChecks.length > 0
  }

  /**
   * Called to add bets to the pot after betting is complete.
   */
  addBetsToPot(pot: Pot = new Pot(0, [], PotKind.MAIN)) {
    logger.trace('[Board.addBetsToPot]')
    pot.amount += this.getBetsTotal()
    logger.trace(`[Board.addBetsToPot] ${pot.amount}`)
    this.clearBets()

    /** if no pot players are specified, assume this is the main pot and copy players */
    if (pot.players.length === 0) {
      pot.players = this.pot.players
    }

    this.addPot(pot)

    this.event$.next(BoardEvents.betsToPot(this))
  }

  /**
   * Called to add bets to the mainpot after betting is complete.
   */
  addBetsToMainPot(pot: Pot = new Pot(0, [], PotKind.MAIN)) {
    pot.amount += this.getBetsTotal()
    this.clearBets()

    /** if no pot players are specified, assume this is the main pot and copy players */
    if (pot.players.length === 0) {
      pot.players = this.pot.players
    }

    this.addMainPot(pot)

    this.event$.next(BoardEvents.betsToPot(this))
  }

  /**
   * Adds the specified pot as a mainpot to the board
   * The first time we create a mainpot
   *
   * @param pot pot to add as a mainpot
   */
  addMainPot(pot: Pot) {
    const exists: Pot | undefined = this.getAllPots().find(
      (existingPot) => JSON.stringify(existingPot.getPlayerIds()) === JSON.stringify(pot.getPlayerIds())
    )

    if (pot.kind === PotKind.MAIN) {
      this.pot.amount += pot.amount
    } else {
      if (exists) {
        exists.amount += pot.amount
      } else {
        pot.kind = PotKind.MAIN
        this.pot = pot
      }
    }
  }

  /**
   * Adds the specified pot as a sidepot to the board
   * The first time we create a sidepot, we set the max main pot
   *
   * @param pot pot to add as a sidepot
   */
  addPot(pot: Pot) {
    const exists: Pot | undefined = this.getAllPots().find(
      (existingPot) => JSON.stringify(existingPot.getPlayerIds()) === JSON.stringify(pot.getPlayerIds())
    )
    if (pot.kind === PotKind.MAIN) {
      this.pot.amount += pot.amount
    } else {
      if (exists) {
        exists.amount += pot.amount
      } else {
        if (this.pot.amount === 0) {
          pot.kind = PotKind.MAIN
          this.pot = pot
        } else {
          pot.kind = PotKind.SIDE
          this.sidePots.unshift(pot)
          this.event$.next(BoardEvents.createSidePot(this))
        }
      }
    }
  }

  /**
   * Aggregate all pots and check for winners with winning hands. If there are winners
   * with winning hands, then everyone did NOT fold. Otherwise, everyone folded.
   * @returns true if everyone folded, false otherwise
   */
  everyoneFolded() {
    const countOfWinningHands: Pot = [this.pot, ...this.sidePots].reduce((winners, pot) => {
      const winningHands: PotWinner[] = pot.winners.filter((winner) => winner.winningCards === null)
      if (winningHands) {
        winners = winners.concat(...winningHands)
        return winners
      }
    }, [])

    if (countOfWinningHands.length === 0) {
      return true
    }
    return false
  }

  clearBets() {
    logger.trace('[Board.clearBets]')
    this.bets = seededSeats.map(() => new Bet(0, BetKind.BLANK))
  }
  /**
   * Get total value of the pots at a table. Useful during
   * betting with all in and side pots. Bet recommendations
   * are based off the total pot amount for all sidepots for
   * continuing players.
   *
   * @returns total value of all pots at the table
   */
  getTotalPotsValue() {
    return (
      this.pot.amount +
      this.sidePots.reduce((acc, pot) => {
        acc += pot.amount
        return acc
      }, 0)
    )
  }

  getBetsTotal() {
    const sum = this.bets
      .filter((b) => b !== null && typeof b !== 'undefined')
      .reduce((acc, value) => {
        return acc + value.amount
      }, 0)
    return sum
  }

  getAllPots() {
    return [this.pot, ...this.sidePots].filter((b) => b !== null && typeof b !== 'undefined')
  }

  getBets() {
    return this.bets
  }

  setBets(bets: Bet[]) {
    this.bets = bets
  }

  internalState() {
    return this.state.tag
  }
}
